import React, { useEffect, useState } from 'react';
import { Download, X, Share2 } from 'lucide-react';
import { BRAND_NAME, LOGO_URL } from '@/lib/api';

const STORAGE_KEY = 'indivalife.install_topbar_dismissed_at';
// Re-show every 6 hours until installed.
const DISMISS_HOURS = 6;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const detectPlatform = () => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const android = /Android/.test(ua);
  const chrome = /Chrome|CriOS/.test(ua) && !/Edg|OPR|SamsungBrowser/.test(ua);
  const isChromium = !!(window.chrome) || /Chrome|Edg|OPR/i.test(ua);
  return { iOS, isIOSSafari: iOS && safari, android, chrome, isChromium };
};

export const InstallTopBar = () => {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_HOURS * 3600 * 1000) return;

    const { isIOSSafari, isChromium, android } = detectPlatform();

    // A prompt may have been captured by the early inline script in index.html
    // before React mounted — pick it up immediately.
    if (window.__deferredInstallPrompt) {
      setEvt(window.__deferredInstallPrompt);
      setIosMode(false);
      setVisible(true);
    }

    const onPrompt = () => {
      setEvt(window.__deferredInstallPrompt);
      setIosMode(false);
      setVisible(true);
    };
    window.addEventListener('pwa-install-available', onPrompt);

    const onInstalled = () => {
      setEvt(null);
      setVisible(false);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    };
    window.addEventListener('pwa-installed', onInstalled);

    // iOS Safari has no beforeinstallprompt — show the manual Share hint.
    if (isIOSSafari) {
      setIosMode(true);
      setVisible(true);
    } else if (android || isChromium) {
      // Always surface the install bar on Android/Chromium so it's never missing.
      // If Chrome has already offered a native prompt it fires a one-tap install;
      // otherwise the button opens a clean how-to-install modal.
      setVisible(true);
    }

    return () => {
      window.removeEventListener('pwa-install-available', onPrompt);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = evt || window.__deferredInstallPrompt;
    if (prompt) {
      // Single-tap native install dialog
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted' || outcome === 'dismissed') {
        window.__deferredInstallPrompt = null;
        setVisible(false);
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
      }
      return;
    }
    // No native prompt available (rare on Android/Chromium) — show quick help.
    setShowHelp(true);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible) return null;

  const { iOS, android } = detectPlatform();

  return (
    <>
      <div
        className="sticky top-0 z-50 bg-gradient-to-r from-[#1D4ED8] via-[#2563EB] to-[#1D4ED8] text-white px-3 py-2.5 flex items-center justify-between gap-2 shadow-lg"
        data-testid="install-top-bar"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <img src={LOGO_URL} alt="" className="h-9 w-9 rounded-md bg-white p-1 object-contain shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm leading-tight truncate">Install {BRAND_NAME} — single tap</div>
            <div className="text-[11px] leading-tight opacity-90 truncate">
              {iosMode ? 'Tap Share, then Add to Home Screen' : 'Get push alerts & offline access on your home screen'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {iosMode ? (
            <button
              onClick={() => setShowHelp(true)}
              className="bg-white text-[#1D4ED8] font-bold px-3 py-1.5 rounded-md text-sm hover:bg-blue-50 active:scale-95 transition-all flex items-center gap-1.5 shadow-md"
              data-testid="install-top-bar-btn"
            >
              <Share2 className="h-3.5 w-3.5" />
              How
            </button>
          ) : (
            <button
              onClick={handleInstall}
              className="bg-white text-[#1D4ED8] font-bold px-3.5 py-1.5 rounded-md text-sm hover:bg-blue-50 active:scale-95 transition-all flex items-center gap-1.5 shadow-md"
              data-testid="install-top-bar-btn"
            >
              <Download className="h-3.5 w-3.5" />
              Install
            </button>
          )}
          <button
            onClick={dismiss}
            aria-label="Dismiss install"
            className="text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10"
            data-testid="install-top-bar-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Fallback help modal — only shown when native prompt isn't available */}
      {showHelp && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setShowHelp(false)}
          data-testid="install-help-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-lg object-contain shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-base">Install {BRAND_NAME}</div>
                <div className="text-xs text-gray-500">Add to your home screen for one-tap access</div>
              </div>
              <button onClick={() => setShowHelp(false)} aria-label="Close" className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="text-sm text-gray-700 space-y-2">
              {iOS ? (
                <>
                  <div>1. Tap the <Share2 className="inline h-4 w-4" /> <b>Share</b> icon at the bottom of Safari.</div>
                  <div>2. Scroll down and tap <b>{'\u201C'}Add to Home Screen{'\u201D'}</b>.</div>
                  <div>3. Tap <b>Add</b> in the top-right corner.</div>
                </>
              ) : android ? (
                <>
                  <div>Chrome should show an <b>Install</b> button automatically. If not:</div>
                  <div>1. Tap the <b>⋮ menu</b> at the top-right of Chrome.</div>
                  <div>2. Tap <b>{'\u201C'}Install app{'\u201D'}</b> or <b>{'\u201C'}Add to Home screen{'\u201D'}</b>.</div>
                  <div>3. Confirm install — INDIVALIFE will appear like a native app on your home screen.</div>
                </>
              ) : (
                <>
                  <div>1. Click the <b>Install</b> icon in your browser address bar (looks like ⊕ or a small monitor).</div>
                  <div>2. Or open the browser menu and pick <b>{'\u201C'}Install INDIVALIFE{'\u201D'}</b>.</div>
                </>
              )}
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="mt-4 w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white font-bold py-2.5 rounded-lg"
              data-testid="install-help-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallTopBar;
